import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { findDOMNode } from 'react-dom';

import cls from 'classnames/bind';

import isEqual from 'lodash/isEqual';

import getFields from '@ott/get-fields-utility';
import isMobile from '@ott/helper-is-mobile';
import Overlay from '@ott/overlay';

import ClosestParent from 'dom-helpers/query/closest';
import $offset from 'dom-helpers/query/offset';
import scrollIntoView from 'scroll-into-view';

import s from './FirstErrorShower.scss';

const TO_TOP_EXTRA_OFFSET = -50;
const TO_BOT_EXTRA_OFFSET = 200;

const cx = cls.bind(s);

const LOCATOR_PREFIXES = {
  avia: (field) => field.split('.')[1],
  default: (field) => field,
};

function getLocatorPrefix(fieldName, product = 'default') {
  const getProductPrefix = LOCATOR_PREFIXES[product];
  return getProductPrefix(fieldName);
}

export const prepareRHFErrorsDeeply = (layer) =>
  Object.keys(layer).reduce((acc, key) => {
    if (Array.isArray(layer[key])) {
      acc[key] = layer[key].map((object) => prepareRHFErrorsDeeply(object));
    } else {
      acc[key] = layer[key].message;
    }

    return acc;
  }, {});

class FirstErrorShower extends PureComponent {
  constructor(props, ctx) {
    super(props, ctx);

    this.handleShowError = this.handleShowError.bind(this);
    this.handleHideError = this.handleHideError.bind(this);

    this.state = {
      error: {},
      showError: false,
      fieldIdx: -1,
    };
    this._fields = [];
  }

  componentWillReceiveProps(nextProps) {
    const newErrors = !isEqual(nextProps.errors, this.props.errors);

    if ((newErrors && nextProps.show) || (nextProps.show && !this._shown)) {
      this._shown = true;
      this.showErrors(nextProps);
    }

    if (!nextProps.show && this.props.show && this._shown) {
      this._shown = false;
      this.tearDown();
    }
    if (nextProps.time !== this.props.time) {
      this._needFocus = true;
    }
  }

  focusFirstInvalid() {
    const { withFocus, canScrollWindow } = this.props;

    this._focusTO = setTimeout(() => {
      const field = this._fields[0];

      if (!field || !field.input || !field.input.focus) {
        return;
      }

      const offset = $offset(field.input);

      const bodyHeight = document.body.offsetHeight - TO_BOT_EXTRA_OFFSET;
      const toBot = offset.top + offset.height > bodyHeight;
      const toTop = offset.top < 0;

      if (!toTop && !toBot && !isMobile()) {
        if (field.input.disabled) {
          this.handleShowError(this._fields.indexOf(field));
        } else {
          field.input.focus();
        }
        return;
      }

      let time = 300;
      if (toTop && offset.top - offset.height > TO_TOP_EXTRA_OFFSET) {
        time = 100;
      } else if (toBot && offset.top + offset.height - bodyHeight < TO_BOT_EXTRA_OFFSET) {
        time = 100;
      }

      scrollIntoView(
        field.input,
        {
          time,
          isScrollable: (target, defaultIsScrollable) => {
            if (canScrollWindow) {
              return defaultIsScrollable(target);
            }
            return target !== window;
          },
        },
        () => {
          if (withFocus) {
            field.input.focus();
            return;
          }
          this.handleShowError(this._fields.indexOf(field));
        }
      );
    }, 0);
  }

  componentDidUpdate() {
    this._updateTO = setTimeout(() => {
      if (this._needFocus) {
        this._needFocus = false;
        if (this._fields.length && this._inputs.indexOf(document.activeElement) === -1) {
          this.focusFirstInvalid();
        } else {
          const validInputInFocus =
            this._inputsByName &&
            document.activeElement &&
            this._fields.indexOf(this._inputsByName[document.activeElement.name]) === -1;
          if (validInputInFocus) {
            this.focusFirstInvalid();
          }
        }
      } else if (this._recheck && this._inputs) {
        this._recheck = false;
        if (~this._inputs.indexOf(document.activeElement)) {
          const field = this._inputsByName[document.activeElement.name];
          const idx = ~this._fields.indexOf(field);
          if (idx) {
            field.handleFocus();
          }
        }
      }
      if (!this.props.show) {
        this.tearDown();
      }
    }, 0);
  }

  componentWillUnmount() {
    clearTimeout(this._hideTO);
    this.tearDown();
  }

  showErrors(nextProps) {
    const { effect, useRHFErrors } = nextProps;

    let fields = getFields(nextProps.errors, useRHFErrors);
    this.tearDown();

    const container =
      typeof nextProps.container === 'function' ? nextProps.container() : nextProps.container;
    this._form = container instanceof HTMLFormElement ? container : findDOMNode(container);
    if (!this._form) {
      console.error('no dom found @ container');
      return;
    }

    if (this._form.tagName !== 'FORM') {
      this._form = this._form.querySelector('form') || ClosestParent(this._form, 'form');
      if (!this._form) {
        console.error('Container should be FORM or it should contain form or it should be in form');
        return;
      }
    }
    let inputs = (this._inputs = this._form.elements); // doesnt work with new RF

    try {
      inputs = this._inputs = this._form.querySelectorAll('[name]');
    } catch (error) {
      console.error('new input getter error', error);
    }

    if (!inputs) {
      console.error('no inputs found');
      return;
    }
    this._inputs = inputs = Array.from(inputs);
    const inputsByName = (this._inputsByName = inputs.reduce((acc, input) => {
      if (input.name) {
        acc[input.name] = input;
      }
      return acc;
    }, {}));

    if (fields && fields.length) {
      fields.map((data) => {
        const input = inputsByName[data.field];
        if (!input) {
          console.error('no input', data && data.field);
          return;
        }
        this._inputsByName[data.field] = data;
        data.input = input;
        data.container = input.parentNode;

        data.handleFocus = () => {
          this.handleShowError(this._fields.indexOf(data));
        };
        input.addEventListener('focus', data.handleFocus);
        input.addEventListener('blur', this.handleHideError);
      });

      if (nextProps.firstIsTop) {
        fields = fields.sort((a, b) => inputs.indexOf(a.input) - inputs.indexOf(b.input));
      }
      this._fields = fields;
      this.setState({ fieldIdx: -1, field: null }); //force rerender;
    }

    this._recheck = true;
  }

  handleShowError(idx) {
    clearTimeout(this._handleHideTO);
    clearTimeout(this._hideTO);
    clearTimeout(this._focusTO);
    this._hideTO = setTimeout(() => {
      this.handleHideError();
    }, 10000);
    this.setState(
      {
        showError: true,
        showAnimation: false,
        fieldIdx: idx,
      },
      () => {
        setTimeout(() => {
          this.setState({
            showAnimation: true,
          });
        });
      }
    );
  }

  handleHideError() {
    clearTimeout(this._handleHideTO);
    this._handleHideTO = setTimeout(() => {
      clearTimeout(this._hideTO);
      this.setState({
        showError: false,
        fieldIdx: -1,
        field: null,
        showAnimation: false,
      });
    }, 50);
  }

  tearDown() {
    if (this._fields) {
      this._fields.forEach((data) => {
        if (data.input) {
          data.input.removeEventListener('focus', data.handleFocus);
          data.input.removeEventListener('blur', this.handleHideError);
        }
        data.handleFocus = null;
        data.input = null;
        data.container = null;
      });
    }
    this._fields = [];
    this._form = null;
    this._inputs = null;
    this._inputsByName = null;
    clearTimeout(this._hideTO);
    clearTimeout(this._focusTO);
    clearTimeout(this._updateTO);
    clearTimeout(this._handleHideTO);
  }

  render() {
    const { focusOnly = false, overlayContainer, product, tooltipClassName } = this.props;
    const { fieldIdx, showError, showAnimation } = this.state;

    let overlayNode = null;
    const field = this._fields[fieldIdx];

    if (focusOnly) {
      return null;
    }

    if (showError && field && field.msg !== '__onlyFocus') {
      const isFn = typeof overlayContainer === 'function';
      const container = isFn ? overlayContainer() : overlayContainer;

      const dataLocator = getLocatorPrefix(field.field, product);

      overlayNode = (
        <Overlay
          container={container}
          targetFn={function () {
            return field.container;
          }}
          show
          placement="top"
        >
          <div
            className={cx('tooltip', {
              [`product-${product}`]: Boolean(product),
              showAnimation,
              [tooltipClassName]: tooltipClassName,
            })}
            data-locator={`error-tooltip-${dataLocator}`}
          >
            <div className={cx('wrap')}>
              {typeof field.msg === 'string' ? (
                <div className={cx('text')} dangerouslySetInnerHTML={{ __html: field.msg }} />
              ) : (
                <div className={cx('text')}>{field.msg}</div>
              )}
            </div>
          </div>
        </Overlay>
      );
    }

    return (
      <div
        className={cx('FirstErrorShower', {
          [`FirstErrorShower--product-${product}`]: Boolean(product),
        })}
      >
        {overlayNode}
      </div>
    );
  }
}

FirstErrorShower.propTypes = {
  children: PropTypes.node,
  firstIsTop: PropTypes.bool,
  errors: PropTypes.object,
  time: PropTypes.any,
  show: PropTypes.bool,
  withFocus: PropTypes.bool,
  canScrollWindow: PropTypes.bool,
  effect: PropTypes.oneOf(['static', 'transition']),
  tooltipClassName: PropTypes.string,
  useRHFErrors: PropTypes.bool,
};

FirstErrorShower.defaultProps = {
  firstIsTop: true,
  withFocus: true,
  canScrollWindow: true,
  effect: 'static',
};

export default FirstErrorShower;
